import React, { useState } from "react";
import { CalendarCheck, Plus, ShieldOff, Syringe } from "lucide-react";

import { ProcessMessagesButton } from "components/ProcessMessagesButton/ProcessMessagesButton";
import { Button, HseDetailsNotice } from "components/ui";
import { Dialog } from "components/ui/Dialog";
import { SidebarMenu } from "components/ui/SidebarMenu";
import { HealthlinkMisconfiguredNotice } from "features/healthlink/components/HealthlinkMisconfiguredNotice";
import { useHealthlinkPracticeStatus } from "features/healthlink/hooks/useHealthlinkPracticeStatus";
import {
  HSE_FIELD,
  HSE_REQUIRED_FIELDS,
} from "features/healthlink/utils/hsePatientReadiness";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";

import { AddVaccineForm } from "./components/AddVaccineForm/AddVaccineForm.jsx";
import { RefusalsTab } from "./components/RefusalsTab";
import { ScheduleTab } from "./components/ScheduleTab";
import { VaccinesTable } from "./components/VaccinesTable/VaccinesTable.jsx";
import s from "./PatientImmunizationsPage.module.css";

// Two views of the same subject, chosen in the left menu the way Documents picks a
// document type: the history is the record, the schedule is the outstanding work. Both
// keep the Add vaccine button, because either can be the thing a clinician is looking at
// when they decide to give a dose (KIN-1842).
const VIEWS = {
  history: "Vaccine History",
  schedule: "Immunisation Schedule",
  // A dose that was declined or clinically withheld is neither history nor outstanding
  // work: it explains why a gap in the schedule is there, so the clinician stops
  // re-asking at every visit (KIN-1849).
  refusals: "Vaccines Not Given",
};

const PatientImmunizationsPage = () => {
  const [activeView, setActiveView] = useState(VIEWS.history);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Set only when the form is opened from a due dose on the schedule, and cleared on
  // close so the next plain "Add vaccine" starts empty (KIN-1842).
  const [prefill, setPrefill] = useState(null);
  const [seletedVaccineType] = useState("single-dose");
  const patientId = useCurrentPatientId();
  const tenantVal = localStorage.getItem("kinesin-tenant");

  // HealthLink features are only usable when a practice certificate is
  // configured; otherwise we prompt for configuration instead of showing
  // broken HealthLink controls.
  const { isConfigured, isMisconfigured } = useHealthlinkPracticeStatus();
  const { data: patient } = usePatientData(patientId);

  return (
    <>
      <SimplePatientPageLayout
        sidebar={
          <SidebarMenu
            testId="immunisations-menu"
            activeItem={activeView}
            items={[
              {
                label: VIEWS.history,
                Icon: Syringe,
                onClick: () => setActiveView(VIEWS.history),
              },
              {
                label: VIEWS.schedule,
                Icon: CalendarCheck,
                onClick: () => setActiveView(VIEWS.schedule),
              },
              {
                label: VIEWS.refusals,
                Icon: ShieldOff,
                onClick: () => setActiveView(VIEWS.refusals),
              },
            ]}
          />
        }
      >
        {/* Above the card rather than inside it, matching where eLab puts the same
            notice: a GP should not have to open Add vaccine to find out that nothing
            can be submitted for this patient (KIN-1733). The HealthLink field list,
            because that is the mode the form opens in. */}
        <HseDetailsNotice
          patient={patient}
          patientId={patientId}
          requiredFields={[...HSE_REQUIRED_FIELDS, HSE_FIELD.country]}
          submissionName="as a Healthlink vaccine"
        />
        <div className={s.mainWrapper}>
          <div className={s.generalInfoTitleWrapper}>
            <h1 className={s.generalInfoTitle}>{activeView}</h1>
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              {isConfigured && (
                <ProcessMessagesButton
                  title="Process vaccine responses"
                  successMessage="Vaccine responses processed successfully!"
                />
              )}
              <Button
                onClick={() => {
                  setPrefill(null);
                  setIsModalOpen(true);
                }}
                data-testid="add-vaccine-button"
                endIcon={Plus}
              >
                Add vaccine
              </Button>
            </div>
          </div>
          {isMisconfigured && <HealthlinkMisconfiguredNotice />}
          {activeView === VIEWS.history && (
            <VaccinesTable
              patientId={patientId}
              tenantVal={tenantVal}
              seletedVaccineType={seletedVaccineType}
            />
          )}
          {activeView === VIEWS.schedule && (
            <ScheduleTab
              patientId={patientId}
              onRecordDose={(values) => {
                setPrefill(values);
                setIsModalOpen(true);
              }}
            />
          )}
          {activeView === VIEWS.refusals && (
            <RefusalsTab patientId={patientId} />
          )}
        </div>
      </SimplePatientPageLayout>
      <Dialog
        open={isModalOpen}
        title="Add new vaccine"
        onClose={() => {
          setIsModalOpen(false);
          setPrefill(null);
        }}
        width={1400}
      >
        <AddVaccineForm
          currentPatientId={patientId}
          closeModal={() => {
            setIsModalOpen(false);
            setPrefill(null);
          }}
          prefill={prefill}
          tenantVal={tenantVal}
          healthlinkEnabled={isConfigured}
        />
      </Dialog>
    </>
  );
};

export { PatientImmunizationsPage };
